// media-delete-modal-ux cluster (Gordon, ISP-9999) — fix #6: when the delete
// modal blocks a file because it's the user's profile picture, offer a way out
// right there instead of sending the user hunting for Settings. Reuses the
// SAME circular cropper the Profile page's Display Image upload uses
// (CropperProfilePhoto) and the same upload/persist primitives
// (handleFileUpload + saveRecentMedia + useUserUpdateProfile) so this isn't a
// second, divergent avatar-upload implementation.
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import * as S from "./MediaLibrary.styled";
import { IconClose } from "./icons";
import CropperProfilePhoto from "../CropperProfilePhoto/CropperProfilePhoto";
import { validateProfileImage } from "../WheelGallery/GallerySettings/children/ProfileBox/children/profileImageValidation";
import { getUserProfilePath, handleFileUpload } from "../../utils/fileUpload";
import { getLoggedInUser } from "../../utils/helpers";
import { saveRecentMedia } from "../../services/api";
import { useUserUpdateProfile } from "../../services/mutations";

// Keep the localStorage `keyCloak` snapshot `utils/helpers#getLoggedInUser`
// reads in sync with the just-saved avatar. Without this, the delete modal's
// profile-picture guard (helpers.js#blockReason) would keep reporting the OLD
// file as the profile picture until the next full login/reload.
function persistProfileImageLocally(url) {
  try {
    const raw = window.localStorage.getItem("keyCloak");
    const store = raw ? JSON.parse(raw) : null;
    if (!store?.user) return;
    window.localStorage.setItem(
      "keyCloak",
      JSON.stringify({
        ...store,
        user: {
          ...store.user,
          profile_image: url,
          user_profile: { ...(store.user.user_profile || {}), profile_image: url },
        },
      })
    );
  } catch (e) {
    // best-effort local sync only — the real PATCH above already succeeded
  }
}

export default function ChangeProfilePictureModal({ open = true, onClose, onSaved }) {
  const fileInputRef = useRef(null);
  const [pickedUrl, setPickedUrl] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const updateProfile = useUserUpdateProfile();

  if (!open) return null;

  const onFileChosen = (e) => {
    const file = e.target.files[0];
    const { ok, error: validationError } = validateProfileImage(file);
    if (!ok) {
      if (validationError) setError(validationError);
      e.target.value = "";
      return;
    }
    setError(null);
    setPickedUrl(URL.createObjectURL(file));
    setShowCropper(true);
    e.target.value = "";
  };

  const onCropped = async (imageFile) => {
    setShowCropper(false);
    setSaving(true);
    setError(null);
    try {
      const userId = getLoggedInUser()?.id;
      const { location } = await handleFileUpload(imageFile, getUserProfilePath({ user_id: userId }));
      await saveRecentMedia(undefined, { type: "0", url: location });
      await updateProfile.mutateAsync({ user_profile: { profile_image: location } });
      persistProfileImageLocally(location);
      onSaved && onSaved(location);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Couldn't update your profile picture. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const onNotCropped = () => setShowCropper(false);

  return (
    <S.Backdrop
      role="dialog"
      aria-modal="true"
      aria-label="Change profile picture"
      data-testid="change-profile-picture-modal"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose && onClose()}
    >
      <S.Modal w={420}>
        <S.ModalHead>
          <div className="t">Change your profile picture</div>
          <button type="button" className="x" aria-label="Close" onClick={onClose} disabled={saving}>
            <IconClose size={20} />
          </button>
        </S.ModalHead>
        <S.ModalBody>
          <div style={{ fontSize: 14, color: "#565556", lineHeight: 1.5, marginBottom: 12 }}>
            Choose a new photo. Once it's saved, this file is no longer your profile picture and you'll be able to delete it.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={onFileChosen}
            aria-hidden="true"
            tabIndex={-1}
            style={{ display: "none" }}
            data-testid="change-profile-picture-file-input"
          />
          {error ? (
            <div data-testid="change-profile-picture-error" role="alert" style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: "#B93636" }}>
              {error}
            </div>
          ) : null}
          {showCropper && (
            <CropperProfilePhoto
              ImageURL={pickedUrl}
              callBackImageCropped={onCropped}
              callBackImageNotCropped={onNotCropped}
            />
          )}
        </S.ModalBody>
        <S.ModalFoot>
          <S.GhostButton onClick={onClose} disabled={saving}>Cancel</S.GhostButton>
          <S.PrimaryButton
            disabled={saving}
            aria-busy={saving}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            data-testid="change-profile-picture-choose"
          >
            {saving ? "Saving…" : "Choose new picture"}
          </S.PrimaryButton>
        </S.ModalFoot>
      </S.Modal>
    </S.Backdrop>
  );
}

ChangeProfilePictureModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
};
